import {Check} from '@mui/icons-material';
import {Box, Button, Grid, Typography} from '@mui/material';
import React, {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import firmaSelectors from 'src/modules/firma/firmaSelectors';
import FirmaService from 'src/modules/firma/firmaService';
import Errors from 'src/modules/shared/error/errors';
import {getRecordId} from 'src/modules/tarea/tareaParcialHelpers';
import {
  collectParcialExecutorIds,
  shouldBlockVerifierAsExecutor,
} from 'src/security/partialVerificationAccess';
import Storage from 'src/security/storage';
import {customButtonStyle} from 'src/theme/schemes/CustomTheme';
import InputSignatureFormItem from 'src/view/shared/form/items/InputSignatureFormItem';
import TextAreaFormItem from 'src/view/shared/form/items/TextAreaFormItem';
import Message from 'src/view/shared/message';
import ConfirmModal from 'src/view/shared/modals/ConfirmModal';

function buildApprovedResultadosHijas(parcial: any) {
  return (parcial?.tareasVinculadas || []).map((tarea) => ({
    tarea: getRecordId(tarea),
    resultado: 'Aprobada',
  }));
}

function FirmaParcialSignPanel(props: {parcial: any; onSigned?: () => void}) {
  const {parcial, onSigned} = props;
  const currentUser = useSelector(authSelectors.selectCurrentUser);
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const hasPermissionToEdit = useSelector(firmaSelectors.selectPermissionToEdit);

  const [saveLoading, setSaveLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const responsableId = getRecordId(parcial?.responsable);
  const isResponsable = responsableId && String(responsableId) === String(currentUser?.id);
  const isPending = parcial?.estado === 'Pendiente' && parcial?.firmada !== true;
  const executorBlocked =
    shouldBlockVerifierAsExecutor(currentTenant?.id) &&
    collectParcialExecutorIds(parcial).includes(String(currentUser?.id));
  const canSign = hasPermissionToEdit && isResponsable && isPending && !executorBlocked;

  const form = useForm({
    mode: 'all',
    defaultValues: {
      firma:
        currentUser?.firmaRef?.downloadUrl ||
        currentUser?.firmaRef?.publicUrl ||
        currentUser?.firma,
      firmaRef: currentUser?.firmaRef || null,
      nota: parcial?.nota || '',
    } as any,
    shouldUnregister: false,
  });

  const onSign = async () => {
    setConfirm(false);
    const resultadosHijas = buildApprovedResultadosHijas(parcial);
    const values = {
      estado: 'Aprobada',
      firmada: true,
      firma: form.getValues('firma'),
      firmaRef: form.getValues('firmaRef'),
      nota: form.getValues('nota') || undefined,
      resultadosHijas,
    };

    setSaveLoading(true);
    try {
      await FirmaService.update(parcial.id, values);
      Message.success(i18n('entities.firma.update.success'));
      onSigned?.();
    } catch (error) {
      if ([400, 404].includes(Errors.errorCode(error))) {
        Message.error(Errors.selectMessage(error));
      } else {
        Message.error(i18n('entities.firma.parcial.signError'));
      }
    } finally {
      setSaveLoading(false);
    }
  };

  if (!canSign) {
    return (
      <Typography variant="body2" color="textSecondary">
        {executorBlocked
          ? i18n('entities.firma.parcial.verifierIsExecutor')
          : i18n('entities.firma.parcial.signUnavailable')}
      </Typography>
    );
  }

  return (
    <FormProvider {...form}>
      <Typography variant="h6" gutterBottom>
        {i18n('common.sign')}
      </Typography>
      <Grid container>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <InputSignatureFormItem
            name="firma"
            signatureRefName="firmaRef"
            storage={Storage.values.userSignatures}
            width={window.innerWidth > 768 ? 360 : 280}
            height={260}
          />
        </Grid>
      </Grid>
      <Box mt={2}>
        <TextAreaFormItem name="nota" label={i18n('entities.firma.fields.nota')} required={false} />
      </Box>
      <Box mt={2}>
        <Button
          style={{width: '100%', maxWidth: 320, ...customButtonStyle}}
          variant="contained"
          color="primary"
          disabled={saveLoading}
          type="button"
          onClick={() => setConfirm(true)}
          startIcon={<Check />}
          size="large">
          {i18n('entities.firma.parcial.signButton')}
        </Button>
      </Box>

      {confirm && (
        <ConfirmModal
          title={i18n('common.areYouSure')}
          onConfirm={onSign}
          onClose={() => setConfirm(false)}
          okText={i18n('common.yes')}
          cancelText={i18n('common.no')}
        />
      )}
    </FormProvider>
  );
}

export default FirmaParcialSignPanel;
